import { useSEO } from "../../hooks/useSEO";
import { seoConfig } from "../../utils/seoConfig";
import { WanderfulHero } from './WanderfulHero';
import { Navbar, Footer } from './LandingPage';

const UniversitiesPage = () => {
  useSEO(seoConfig.universities);
  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <WanderfulHero
        title="Same exam. Different professors. Different marks? Not anymore."
        subtitle="Grade thousands of papers consistently. Defend every mark. No surprises."
        cta1Text="Schedule a consultation"
        cta2Text=""
      />
      
      {/* Case Study Centerpiece */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-black text-white rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16">
            <div className="text-center mb-12">
              <span className="text-sm font-bold text-white/60 uppercase tracking-widest bg-white/10 px-4 py-1.5 rounded-full">Real Story</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-4">Student Complained. We Reviewed. Marks Changed.</h2>
              <p className="text-white/70 max-w-2xl mx-auto font-medium">A student challenged her grade. Faculty was divided. We re-evaluated the paper against the rubric and found the professor had missed key points. We had proof.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 text-sm text-white/80">
              <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                <h3 className="font-bold text-lg mb-2 text-white">Without Us</h3>
                <p>Debate between the student and professor. Other faculty weighing in. Appeals process takes weeks. Nothing gets resolved.</p>
              </div>
              <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                <h3 className="font-bold text-lg mb-2 text-white">With Us</h3>
                <p>We re-evaluated it. Had the audit trail. Showed exactly where marks were missed. Everyone saw it. Case closed. No appeal needed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Emphasis as Bento Grid (Dark Mode) */}
      <section className="py-8 sm:py-12 md:py-24 bg-black text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-bold text-white/50 uppercase tracking-widest">How It Works</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-4">Consistency. Accountability. Done.</h2>
            <p className="text-gray-400 max-w-2xl mx-auto font-medium">Every paper marked the same way. Every decision tracked. No debates.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {/* Card 1: Large */}
            <div className="md:col-span-2 p-10 bg-neutral-900 rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col justify-between group cursor-pointer">
              <div>
                <h3 className="font-bold text-2xl mb-3 text-white">Same Rubric. Same Marks. Always.</h3>
                <p className="text-neutral-400 text-sm leading-relaxed max-w-md">Whether Prof. Sharma or Prof. Khan grades it, same paper = same marks. No bias. No favoritism. Just the rubric.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">01</span>
            </div>

            {/* Card 2: Tall */}
            <div className="p-10 bg-neutral-900 rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col justify-between group cursor-pointer">
              <div>
                <h3 className="font-bold text-2xl mb-3 text-white">Prove It. We Have Receipts.</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">Student says "This should've been 50." You show them exactly why it's 40. They see the logic. Appeal goes away.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">02</span>
            </div>

            {/* Card 3: Wide */}
            <div className="md:col-span-3 p-10 bg-neutral-900 rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between group cursor-pointer">
              <div className="max-w-xl">
                <h3 className="font-bold text-2xl mb-3 text-white">Whatever Scale. Any Exam Type.</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">Mid-semester assignment with 200 papers? Year-end exam with 10,000 papers? We grade the same way. No shortcuts.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">03</span>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Emphasis */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-[#f9f9f9] rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-slate-900">Built for the Audit Inspector</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">UGC aligned. ISO 27001 certified. Every paper logged. Every mark justified. Show it all. Nothing to hide.</p>
            <div className="flex justify-center gap-4 text-xs font-bold uppercase tracking-wider">
              <span className="bg-gray-100 text-slate-900 px-4 py-2 rounded-full">UGC Aligned</span>
              <span className="bg-gray-100 text-slate-900 px-4 py-2 rounded-full">ISO 27001</span>
              <span className="bg-gray-100 text-slate-900 px-4 py-2 rounded-full">Audit Logs</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UniversitiesPage;
